import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { setThreeDState, setFullScreenState } from 'store/slices/ToolSlice';
import { setSavingState } from 'store/slices/SaveDesignSlice/SaveDesignSlice';
import { setIrrandiance } from 'store/slices/IrradianceSlice';
import { appToast } from 'components/Basic/CustomToast';
import { PARENT_EVENTS } from 'constants/index';
import { postFullViewToggle, postSaveDesign, postSaveDesignTrigger } from 'parentEvents';
import { useRefs } from 'contexts/RefContext';
import { waitForBaseRasterImage } from 'components/tool/utils/konvaUtil';
import { useSaveSolarDesign } from './useSaveSolarDesign';
import { useSaveRoofDesign } from './useSaveRoofDesign';

export function useSaveDesign(designType: DesignType) {
	const dispatch = useDispatch<AppDispatch>();
	const isSolar = designType === 'SOLAR';

	const isB2C = useSelector((state: RootState) => !!state.roofData.data.org?.setting?.isB2C);

	const { konvaRef } = useRefs();
	const { saveSolarDesign, status: solarApiStatus } = useSaveSolarDesign();
	const { saveRoofDesign } = useSaveRoofDesign();

	const disableFullView = useCallback(() => {
		dispatch(setFullScreenState({ enabled: false }));
		postFullViewToggle(false);
	}, [dispatch]);

	const dispatchSaveEvent = useCallback(async () => {
		try {
			// the satellite/aerial background (RasterImage) loads async — capturing the stage
			// before it lands sends the parent a design image with the house background missing.
			const stage = konvaRef?.current;
			if (stage) await waitForBaseRasterImage(stage);
			const detail = isSolar ? await saveSolarDesign() : await saveRoofDesign();
			console.log(detail);
			postSaveDesign(detail);
			if (isB2C) dispatch(setSavingState(false));
		} catch {
			appToast.error('Something went wrong, please try again');
			dispatch(setSavingState(false));
		}
	}, [isSolar, saveSolarDesign, saveRoofDesign, isB2C, dispatch, konvaRef]);

	const save = useCallback(() => {
		if (isSolar) dispatch(setIrrandiance({ showIrradiance: false }));
		dispatch(setThreeDState({ enabled: false }));
		disableFullView();
		dispatch(setSavingState(true));
		postSaveDesignTrigger();
		dispatchSaveEvent();
	}, [isSolar, dispatch, disableFullView, dispatchSaveEvent]);

	useEffect(() => {
		function handleSaveDesignEvent(event: MessageEvent) {
			// ----------------- event by parent for save design --------------
			if (event.data === PARENT_EVENTS.SAVE_DESIGN) {
				dispatchSaveEvent();
			}
		}
		window.addEventListener('message', handleSaveDesignEvent);
		return () => window.removeEventListener('message', handleSaveDesignEvent);
	}, [dispatchSaveEvent]);

	return {
		save,
		status: isSolar ? solarApiStatus : { isLoading: false, isError: false },
	};
}
